// ===========================================================================
// DEBUG PANEL — never shown unless the page is loaded with ?heatDebug.
// Two sets of sliders over the heat burn of the ship: the dish numbers, fitted
// against the ship art with the wireframe on, and the look of the burn itself,
// tuned with the wireframe off. Self-activating, js/index.js picks every
// *Debug.js up on its own. It reads the effect off window.app.shipHeatGlow, so
// no production code references it.
//
// "Jump to align" drops straight into the entry burn from wherever the app is,
// skipping the run before it. "Pause game" freezes everything but the burn, so
// the action never times out while the sliders are being worked. "Force burn
// on" lights it where it stands, without a stage.
//
// Copy the readout at the bottom back into ShipHeatGlow.js when it looks right.
// ===========================================================================

const DISH_PARAMS = [
  ["dishX", 0, 1067, 1],
  ["dishY", 0, 1067, 1],
  ["dishA", 20, 1200, 1],
  ["dishB", 10, 600, 1],
  ["dishAngleDeg", -180, 180, 0.1],
  ["arcFromDeg", -180, 360, 1],
  ["arcToDeg", -180, 360, 1]
];

// How hard the burn reads, as opposed to where it sits. These take effect on
// the next frame, except the palette, which is baked into the sprites.
const LOOK_PARAMS = [
  ["maxIntensity", 0, 1, 0.01],
  ["intensity", 0, 1, 0.01],
  ["emitPerSecond", 0, 400, 5],
  ["puffAlpha.min", 0, 1.5, 0.01],
  ["puffAlpha.max", 0, 2, 0.01],
  ["hazeAlpha", 0, 2, 0.01],
  ["rimAlpha", 0, 1.5, 0.01],
  ["rimCoreAlpha", 0, 1.5, 0.01],
  ["rimSize", 40, 600, 5],
  ["stretchMin", 1, 6, 0.1],
  ["stretchMax", 1, 16, 0.1],
  ["lifeMin", 0.2, 6, 0.1],
  ["lifeMax", 0.4, 10, 0.1],
  ["radialSpeed.min", 0, 1, 0.01],
  ["radialSpeed.max", 0, 1.5, 0.01],
  ["driftSpeed.min", 0, 600, 5],
  ["driftSpeed.max", 0, 900, 5],
  ["hazeAt", 0, 1, 0.01]
];

// The effect keeps its two angles in radians. The sliders work in degrees, since
// that is what the source comments and the measuring are done in. Everything
// else is read straight off the effect, through a dotted path for the pairs.
function readParam(glow, key) {
  if (key === "dishAngleDeg") return glow.dishAngle * 180 / Math.PI;
  if (key === "arcFromDeg") return glow.arcFrom * 180 / Math.PI;
  if (key === "arcToDeg") return glow.arcTo * 180 / Math.PI;

  return key.split(".").reduce((node, step) => node[step], glow);
}

function writeParam(glow, key, value) {
  if (key === "dishAngleDeg") return void (glow.dishAngle = value * Math.PI / 180);
  if (key === "arcFromDeg") return void (glow.arcFrom = value * Math.PI / 180);
  if (key === "arcToDeg") return void (glow.arcTo = value * Math.PI / 180);

  const steps = key.split(".");
  const last = steps.pop();

  steps.reduce((node, step) => node[step], glow)[last] = value;
}

// Straight into the entry burn from wherever the app is: screen 2, the stage the
// align action plays on, the sky it plays under, the burn lit, and the action
// itself launched by hand. Every transition of the run before it is skipped, so
// nothing that would normally have happened by now has.
function jumpToAlign(app) {
  const game = app.game;
  const sequence = game.getSequence();
  const index = sequence.findIndex((entry) => entry.type === "align-ship");
  if (index < 0) return;

  const config = sequence[index];

  const launch = () => {
    // Whatever the screen intro was about to start, it is not this.
    app.killTimeline(app.currentTimeline);
    game.stop();

    game.running = true;
    game.runId++;
    game.score = 0;
    game.maxScore = 0;
    game.actionIndex = index;

    app.resetGauge();
    app.showGauges(true, 0);
    game.$actionContainer.css({opacity: 1, visibility: "visible"});

    app.fadeSkyToAtmosphere(0.4);
    app.setStage(config.stage, true);

    // The ship has to be where the entry leaves it: shield out, flown onto the
    // path, with the path and the target up around it.
    app.playTakeover("deploy-heat-shield");
    app.showAlignFrame(true, 0.4);
    app.alignShipWithEntryArt(0.4);
    app.shipHeatGlow.buildEnter(0.6);

    game.launchAction(config);
  };

  if (parseInt(app.currentScreen) === 2) launch();
  else app.goToScreen(2, launch);
}

// The page styles every button with the display font size and leaves its color
// to the browser, which on this panel is black text on a black ground at forty
// odd pixels. So these say everything themselves.
function makeButton(label, onClick) {
  const button = document.createElement("button");

  button.textContent = label;
  button.style.cssText =
    "display:block;width:100%;margin:8px 0 0;padding:6px;border:0;border-radius:4px;" +
    "font:bold 12px/1.4 monospace;color:#000;background:#3EF0FF;cursor:pointer";
  button.addEventListener("click", onClick);

  return button;
}

function buildPanel(glow) {
  const panel = document.createElement("div");
  panel.style.cssText =
    "position:fixed;top:12px;right:12px;z-index:99999;background:rgba(0,0,0,.8);" +
    "color:#fff;font:12px/1.4 monospace;padding:12px 14px;border-radius:8px;width:330px;" +
    "max-height:calc(100vh - 24px);overflow:auto";

  const readout = document.createElement("pre");
  readout.style.cssText = "margin:10px 0 0;white-space:pre-wrap;color:#3EF0FF;font-size:11px";

  const refresh = () => {
    readout.textContent =
      `this.dishX = ${Math.round(glow.dishX)};\n` +
      `this.dishY = ${Math.round(glow.dishY)};\n` +
      `this.dishAngle = ${(glow.dishAngle * 180 / Math.PI).toFixed(1)} * Math.PI / 180;\n` +
      `this.dishA = ${Math.round(glow.dishA)};\n` +
      `this.dishB = ${Math.round(glow.dishB)};\n` +
      `this.arcFrom = ${Math.round(glow.arcFrom * 180 / Math.PI)} * Math.PI / 180;\n` +
      `this.arcTo = ${Math.round(glow.arcTo * 180 / Math.PI)} * Math.PI / 180;\n` +
      `\n` +
      `this.maxIntensity = ${glow.maxIntensity};\n` +
      `this.emitPerSecond = ${glow.emitPerSecond};\n` +
      `this.lifeMin = ${glow.lifeMin};\n` +
      `this.lifeMax = ${glow.lifeMax};\n` +
      `this.hazeAt = ${glow.hazeAt};\n` +
      `this.puffAlpha = {min: ${glow.puffAlpha.min}, max: ${glow.puffAlpha.max}};\n` +
      `this.hazeAlpha = ${glow.hazeAlpha};\n` +
      `this.radialSpeed = {min: ${glow.radialSpeed.min}, max: ${glow.radialSpeed.max}};\n` +
      `this.driftSpeed = {min: ${glow.driftSpeed.min}, max: ${glow.driftSpeed.max}};\n` +
      `this.stretchMin = ${glow.stretchMin};\n` +
      `this.stretchMax = ${glow.stretchMax};\n` +
      `this.rimSize = ${glow.rimSize};\n` +
      `this.rimAlpha = ${glow.rimAlpha};\n` +
      `this.rimCoreAlpha = ${glow.rimCoreAlpha};`;
  };

  const wireRow = document.createElement("label");
  wireRow.style.cssText = "display:flex;align-items:center;gap:8px;font:bold 12px/1.4 monospace";
  const wire = document.createElement("input");
  wire.type = "checkbox";
  wire.checked = false;
  wire.addEventListener("change", () => glow.setDebug(wire.checked));
  wireRow.append(wire, document.createTextNode("Wireframe"));

  const jump = makeButton("Jump to align", () => jumpToAlign(window.app));

  // The burn is left running while everything else is held, otherwise the
  // sliders would have nothing moving to show their effect on.
  const pause = makeButton("Pause game", () => {
    const app = window.app;

    if (app.isGameplayPaused()) {
      app.resumeGameplay();
      pause.textContent = "Pause game";
      return;
    }

    app.pauseGameplay();

    // The freeze above takes the burn with it, and its build tween is held
    // wherever it had got to, so both are put back by hand: the effect is the
    // one thing that has to keep moving while the sliders are worked.
    glow.setPaused(false);
    glow.startAmbient();
    glow.intensity = glow.maxIntensity;

    pause.textContent = "Resume game";
  });

  const force = makeButton("Force burn on", () => {
    glow.sizeCanvases();
    glow.startAmbient();
    glow.intensity = glow.maxIntensity;

    [glow.$glow, glow.$haze].forEach(($canvas) => {
      $canvas.css({visibility: "visible", opacity: 1});
    });
  });

  panel.append(wireRow, jump, pause, force);

  const addSliders = (params, title) => {
    const heading = document.createElement("div");
    heading.style.cssText = "margin:10px 0 4px;font-weight:bold;color:#FF3EA5";
    heading.textContent = title;
    panel.append(heading);

    params.forEach(([key, min, max, step]) => {
      const row = document.createElement("label");
      row.style.cssText = "display:flex;align-items:center;gap:8px";

      const name = document.createElement("span");
      name.style.cssText = "flex:0 0 104px;font-size:11px;overflow:hidden;text-overflow:ellipsis";
      name.textContent = key.replace("Deg", "°");

      const slider = document.createElement("input");
      slider.type = "range";
      slider.min = min;
      slider.max = max;
      slider.step = step;
      slider.value = readParam(glow, key);
      slider.style.flex = "1";

      const decimals = step < 0.1 ? 2 : (step < 1 ? 1 : 0);

      const val = document.createElement("span");
      val.style.cssText = "flex:0 0 46px;font-size:11px;text-align:right";
      val.textContent = Number(slider.value).toFixed(decimals);

      slider.addEventListener("input", () => {
        const v = parseFloat(slider.value);
        val.textContent = v.toFixed(decimals);
        writeParam(glow, key, v);
        refresh();
      });

      row.append(name, slider, val);
      panel.append(row);
    });
  };

  addSliders(DISH_PARAMS, "Dish");
  addSliders(LOOK_PARAMS, "Look");

  panel.append(readout);
  refresh();

  document.body.appendChild(panel);
}

function init() {
  if (!new URLSearchParams(window.location.search).has("heatDebug")) return;

  const tryAttach = () => {
    const glow = window.app && window.app.shipHeatGlow;
    if (!glow) return setTimeout(tryAttach, 200);

    buildPanel(glow);
  };

  tryAttach();
}

init();
